import { Box, Center, HStack, TabList } from '@chakra-ui/react';
import React from 'react';
import { Handle, NodeProps, Position } from 'reactflow';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';
import type {
  TapeBuilderNodeAbstract,
  TapeBuilderNodeType,
} from '@finley-technologies/tape-builder';

import { useQAContext } from './calcAgentQA/QAContext';
import {
  useGraph,
  getGraphNodeById,
  getArtifactInstance,
} from './GraphContext';
import { OperationNode } from './OperationNode';
import { RenderNodeProps } from './RenderNodeProps';
import { TapeNode } from './TapeNode';
import { TimeCounterAlert } from './TimeCounter';
import { BorrowingBasePipelineInstanceStatusEnumGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';

const mapArtifactStatusToAlertType = (
  status: BorrowingBasePipelineInstanceStatusEnumGQLType,
) => {
  switch (status) {
    case 'inProgress':
      return 'info';
    case 'initializing':
      return 'info';
    case 'error':
      return 'error';
    case 'success':
      return 'success';
    default:
      return finleyAssertUnreachable(status);
  }
};

export const CommonNodeDisplay = (props: RenderNodeProps) => {
  const { nodeType } = props;
  const uiNode = TapeBuilderUI.getUINodeByType(nodeType);
  return uiNode.isOperation ? (
    <OperationNode {...props} />
  ) : (
    <TapeNode {...props} />
  );
};

type NewNodeDisplayProps = {
  nodeText: string;
  nodeType: TapeBuilderNodeType;
};
export const NewNodeDisplay = ({ nodeText, nodeType }: NewNodeDisplayProps) => {
  return (
    <Center px="3">
      <CommonNodeDisplay nodeText={nodeText} nodeType={nodeType} draggable />
    </Center>
  );
};

// Header that sits on top of a node's editor Tabs: it places the shared node
// visual (identical to canvas / add-node palette) next to the tab list, so the
// node identity never needs a separate title band. `children` are the <Tab>s.
export const NodeTabHeader = ({
  node,
  children,
}: {
  node: TapeBuilderNodeAbstract;
  children: React.ReactNode;
}): React.ReactElement => {
  return (
    <HStack align="flex-end" spacing={4}>
      <CommonNodeDisplay
        nodeText={TapeBuilderUI.getFriendlyNameForNode(node)}
        nodeType={node.metadata.tapeBuilderNodeType}
      />
      <TabList flex={1}>{children}</TabList>
    </HStack>
  );
};

export const CommonNodeDisplayWithHandles = ({
  data,
  selected,
}: NodeProps<string>) => {
  // tape displays a check box when it has tape uploaded.
  const graphContext = useGraph();
  const { hasBuffer, hasAllInputBuffers } = useQAContext();
  const tapeBuilderNode = getGraphNodeById(graphContext, data);

  if (!tapeBuilderNode) {
    return <Box>Node: ${data} not found </Box>;
  }

  const artifactInstance = getArtifactInstance({
    tapeBuilderNode,
    graphContext,
  });

  const updatedAt = artifactInstance ? artifactInstance.updated_at : 0;

  const status = artifactInstance
    ? mapArtifactStatusToAlertType(artifactInstance.status)
    : 'info';

  const nodeId = data;
  const { tapeBuilderNodeType: nodeType } = tapeBuilderNode.metadata;
  const uiNode = TapeBuilderUI.getUINodeByType(nodeType);
  const hasInputs = uiNode.isOperation
    ? hasAllInputBuffers(tapeBuilderNode.metadata.tapeBuilderNodeId)
    : hasBuffer(nodeId);
  const nodeText = uiNode.getFriendlyName(tapeBuilderNode);

  const isRaw = !uiNode.isOperation && uiNode.isRaw;
  const isFinal = !uiNode.isOperation && uiNode.isFinal;
  return (
    <>
      {!isRaw && <Handle type="target" position={Position.Left} />}
      <CommonNodeDisplay
        nodeText={nodeText}
        nodeType={nodeType}
        hasInputs={hasInputs}
        selected={selected}
        nodeId={nodeId}
      >
        {updatedAt !== 0 && (
          <TimeCounterAlert status={status} startTime={updatedAt} />
        )}
      </CommonNodeDisplay>
      {!isFinal && <Handle type="source" position={Position.Right} />}
    </>
  );
};
